import React, { useCallback, memo, useEffect, useState } from 'react';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { OPTIMISTIC_ID_PREFIX } from '@atlassian/jira-business-list-handlers';
import {
	DEPENDENCY_HOVER_NUDGE_STEP,
	useNudgeStep,
} from '@atlassian/jira-business-onboarding-timeline-dependencies';
import { ff } from '@atlassian/jira-feature-flagging';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl';
import { toIssueId } from '@atlassian/jira-shared-types';
import { isBaseLevel } from '@atlassian/jira-software-roadmap-model';
import { useViewport, useTimelineState } from '@atlassian/jira-software-roadmap-timeline-table';
import {
	RoadmapChartItemContent,
	EXPLICIT,
	getBarLeftPosition,
	getBarRightPosition,
} from '@atlassian/jira-software-roadmap-timeline-table-kit';
import type {
	OnChartItemClick,
	OnChartItemUpdate,
	Placeholder,
} from '@atlassian/jira-software-roadmap-timeline-table-kit/src/types';
import { CHART_BAR_RESIZE } from '@atlassian/jira-software-roadmap-utils/src/utils/metrics';
import { DndLongTaskMetric } from '../../../common/ui/metrics';
import { getColor } from '../../../common/utils/get-color';
import { useIssuesMap } from '../../../common/utils/use-issues-map';
import { useTimelineFeatures } from '../../../controllers/features-context';
import type { TimelineViewIssue } from '../../../types';
import { renderBarContent } from './bar-content';
import { START_DATE_LATER_DUE_DATE_MESSAGE_ID } from './constants';

type ChartItemContentProps = {
	dueDate?: number;
	isExportTable: boolean;
	isHovered: boolean;
	isReadOnly: boolean;
	issue: TimelineViewIssue;
	level: number;
	onItemUpdate: OnChartItemUpdate;
	placeholder: Placeholder;
	setSelectedIssueKey: (arg1: string) => void;
	startDate?: number;
};
type DndLongTaskMetricProps = {
	isDragging: boolean;
	level: number;
};

const DndLongTaskMetricComponent = ({ level, isDragging }: DndLongTaskMetricProps) => (
	<DndLongTaskMetric
		isDragging={isDragging}
		metricType={CHART_BAR_RESIZE}
		extraProps={{ isBaseLevel: isBaseLevel(level) }}
	/>
);

export const ChartItemContent = memo<ChartItemContentProps>((props: ChartItemContentProps) => {
	const {
		isExportTable,
		isHovered,
		isReadOnly,
		issue,
		level,
		onItemUpdate,
		placeholder,
		setSelectedIssueKey,
	} = props;
	let { dueDate, startDate } = props;

	const warnings: MessageDescriptorV2[] = [];
	if (
		ff('timeline-bar-start-date-after-due-date_ddpds') &&
		dueDate !== undefined &&
		startDate !== undefined &&
		dueDate < startDate
	) {
		warnings.push(START_DATE_LATER_DUE_DATE_MESSAGE_ID);
		[startDate, dueDate] = [dueDate, startDate];
	}

	const { status, issueId } = issue;
	const color = getColor(status.categoryId);
	const [{ issuesMap }] = useIssuesMap();
	const [{ nudgeStep }] = useNudgeStep();
	const { requestViewportScrollBounding } = useViewport();
	const [{ timelineDuration, timelineWidth }] = useTimelineState();
	const [shouldRenderTimelineNudge, setShouldRenderTimelineNudge] = useState(false);
	const { issueViewInteraction } = useTimelineFeatures();

	const onUpdate = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type. | TS7006 - Parameter 'param' implicitly has an 'any' type. | TS7006 - Parameter 'analyticEvent' implicitly has an 'any' type.
		(id, param, analyticEvent) => {
			onItemUpdate(toIssueId(id), param, analyticEvent);
		},
		[onItemUpdate],
	);

	/*
	 * We should show timeline dependency nudge only if the following conditions are met:
	 * - current bar is first issue and in view
	 * - there's at least 2 bars
	 * - the current nudge step is DEPENDENCY_HOVER_NUDGE_STEP
	 * The following function determines whether we should show it or not based on them
	 */
	const setShouldRenderHoverNudge = useCallback(() => {
		const viewportBoundingClient = requestViewportScrollBounding();

		if (viewportBoundingClient) {
			const { scrollLeft, scrollRight } = viewportBoundingClient;
			const [firstItem, secondItem] = Array.from(issuesMap.values());
			const isFirstItem = issueId === firstItem?.issueId;
			const timelineHasTwoBars =
				issuesMap.size > 1 &&
				firstItem?.startDate != null &&
				firstItem?.dueDate != null &&
				secondItem.startDate != null &&
				secondItem?.dueDate != null;
			let isBarInView = false;

			if (startDate != null && dueDate != null) {
				const leftPosition = getBarLeftPosition(startDate, timelineDuration, timelineWidth);
				const rightPosition = getBarRightPosition(dueDate, timelineDuration, timelineWidth);

				const isBarInPast = leftPosition < scrollLeft;
				const isBarInFuture = rightPosition < scrollRight;
				isBarInView = !isBarInPast && !isBarInFuture;
			}

			const shouldRenderHoverNudge =
				isFirstItem &&
				timelineHasTwoBars &&
				nudgeStep === DEPENDENCY_HOVER_NUDGE_STEP &&
				isBarInView;

			setShouldRenderTimelineNudge(shouldRenderHoverNudge);
		}
	}, [
		requestViewportScrollBounding,
		dueDate,
		startDate,
		issueId,
		issuesMap,
		nudgeStep,
		timelineDuration,
		timelineWidth,
	]);

	/*
	 * Viewport information is only available after all components are rendered
	 * We therefore need to trigger requestViewportScrollBounding a second time after initial render
	 * to correctly receive the scroll positions
	 * For more information: software/roadmap/timeline-table/src/common/context/viewport/README.md
	 */
	useEffect(() => {
		setShouldRenderHoverNudge();
	}, [setShouldRenderHoverNudge]);

	const leftClickHandler = useCallback<OnChartItemClick>(
		(_id, meta) => {
			if (issue.id.startsWith(OPTIMISTIC_ID_PREFIX)) {
				return;
			}
			if (issueViewInteraction === 'openNewTab' || meta.withCmd || meta.withShift) {
				window.open(`/browse/${issue.key}`, '_blank');
				return;
			}
			setSelectedIssueKey(issue.key);
		},
		[issue.id, issue.key, issueViewInteraction, setSelectedIssueKey],
	);

	const content = (
		<RoadmapChartItemContent
			renderBarContent={isExportTable ? undefined : renderBarContent}
			onUpdate={onUpdate}
			level={level}
			isHovered={isHovered}
			id={issueId}
			color={color}
			startDate={startDate}
			dueDate={dueDate}
			startDateType={EXPLICIT}
			dueDateType={EXPLICIT}
			placeholder={placeholder}
			DndLongTaskMetric={DndLongTaskMetricComponent}
			isReadOnly={isReadOnly}
			onLeftClick={leftClickHandler}
			{...(ff('timeline-bar-start-date-after-due-date_ddpds') && {
				warnings,
			})}
		/>
	);

	if (shouldRenderTimelineNudge) {
		return <SpotlightTarget name={DEPENDENCY_HOVER_NUDGE_STEP}>{content} </SpotlightTarget>;
	}

	return content;
});
